import React, { useState, useEffect, useRef } from "react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Clock,
  TrendingUp,
  Flame,
  Check,
  Plus,
  Send,
  Sparkles,
  CheckCircle2,
  MessageSquare,
  MapPin,
  Mail
} from "lucide-react";
import { roadmapService, getVoterEmail, setVoterEmail } from "../utils/roadmapService";
import { getFallbackBackView } from "../utils/navigationHelper";

const STATUS_OPTIONS = [
  { id: "Requested", label: "Requested", dotColor: "bg-emerald-500", badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { id: "Ideas", label: "Ideas", dotColor: "bg-teal-500", badgeBg: "bg-teal-50 text-teal-700 border-teal-200" },
  { id: "Planned", label: "Planned", dotColor: "bg-blue-600", badgeBg: "bg-blue-50 text-blue-700 border-blue-200" },
  { id: "In progress", label: "In progress", dotColor: "bg-purple-600", badgeBg: "bg-purple-50 text-purple-700 border-purple-200" },
  { id: "Released", label: "Released", dotColor: "bg-emerald-600", badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-200" }
];

const SORT_OPTIONS = [
  { id: "recent", label: "Recent", icon: Clock },
  { id: "trending", label: "Trending", icon: TrendingUp },
  { id: "most_voted", label: "Most Voted", icon: Flame }
];

function Roadmap({
  setView,
  setActiveIcon,
  setupDetails,
  setIsOpen,
  widgetConfigs
}) {
  const partnerId =
    widgetConfigs?.uniquePartnerId ||
    setupDetails?.uniquePartnerId ||
    localStorage.getItem("uniquePartnerId");

  const primaryColor =
    setupDetails?.widgetCustomizations?.color2 ||
    setupDetails?.widgetCustomizations?.uiColor ||
    setupDetails?.widgetCustomizations?.color1 ||
    "#000000";

  const partnerName =
    setupDetails?.widgetCustomizations?.partnerName || "";

  // Mode: 'feed' | 'request' | 'detail'
  const [mode, setMode] = useState("feed");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters & Sorting
  const [activeStatus, setActiveStatus] = useState("Requested");
  const [activeSort, setActiveSort] = useState("recent");
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);

  // Detail item
  const [selectedItem, setSelectedItem] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isDescExpanded, setIsDescExpanded] = useState(false);

  // Conversational "Request a feature" flow state
  const [step, setStep] = useState(1); // 1: title/idea, 2: details & email, 3: matching requests / submit
  const [ideaText, setIdeaText] = useState("");
  const [submittedIdea, setSubmittedIdea] = useState("");
  const [moreDetails, setMoreDetails] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [isBotTyping, setIsBotTyping] = useState(false);

  const getInitialEmail = () => {
    const voterEmail = getVoterEmail();
    if (voterEmail) return voterEmail;
    try {
      const cust = JSON.parse(
        localStorage.getItem("dexkor-chat-widget-customer-details")
      );
      if (cust?.email) return cust.email;
    } catch (e) {}
    if (widgetConfigs?.userEmail || widgetConfigs?.email) return widgetConfigs.userEmail || widgetConfigs.email;
    return "";
  };

  const [userEmail, setUserEmail] = useState(getInitialEmail());
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [similarItems, setSimilarItems] = useState([]);
  const [isSearchingMatches, setIsSearchingMatches] = useState(false);
  const [isSubmittingIdea, setIsSubmittingIdea] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);

  const scrollRef = useRef(null);
  const chatScrollRef = useRef(null);

  // Scroll to bottom when conversation advances or bot is typing
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [step, isBotTyping, submittedIdea, submittedEmail, similarItems, submissionSuccess]);

  // Fetch roadmap items
  const loadItems = async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      const data = await roadmapService.getPublicRoadmap(
        partnerId,
        activeStatus,
        activeSort
      );
      setItems(data || []);
    } catch (err) {
      console.error("Failed to load roadmap:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [partnerId, activeStatus, activeSort]);

  // Handle vote toggle
  const handleVoteToggle = async (e, item) => {
    if (e) e.stopPropagation();
    if (!item?._id || !partnerId) return;

    const currentlyVoted = item.hasVoted;
    const currentCount = Number(item.voteCount || 0);
    const newCount = currentlyVoted ? Math.max(0, currentCount - 1) : currentCount + 1;

    setItems((prev) =>
      prev.map((it) =>
        it._id === item._id ? { ...it, hasVoted: !currentlyVoted, voteCount: newCount } : it
      )
    );

    setSimilarItems((prev) =>
      prev.map((it) =>
        it._id === item._id ? { ...it, hasVoted: !currentlyVoted, voteCount: newCount } : it
      )
    );

    if (selectedItem?._id === item._id) {
      setSelectedItem((prev) => ({ ...prev, hasVoted: !currentlyVoted, voteCount: newCount }));
    }

    try {
      const result = await roadmapService.toggleVote(partnerId, item._id);
      if (result) {
        setItems((prev) =>
          prev.map((it) =>
            it._id === item._id
              ? { ...it, hasVoted: result.hasVoted, voteCount: result.voteCount }
              : it
          )
        );
        setSimilarItems((prev) =>
          prev.map((it) =>
            it._id === item._id
              ? { ...it, hasVoted: result.hasVoted, voteCount: result.voteCount }
              : it
          )
        );
        if (selectedItem?._id === item._id) {
          setSelectedItem((prev) => ({
            ...prev,
            hasVoted: result.hasVoted,
            voteCount: result.voteCount
          }));
        }
      }
    } catch (err) {
      console.error("Vote failed:", err);
    }
  };

  // Submit comment in detail view
  const handleSendComment = async () => {
    if (!commentText.trim() || !selectedItem?._id || !partnerId) return;

    let emailToUse = userEmail;
    if (!emailToUse) {
      if (!emailInput.trim()) {
        setEmailError("Please enter your email to comment");
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailInput.trim())) {
        setEmailError("Please enter a valid email address");
        return;
      }
      emailToUse = emailInput.trim();
      setVoterEmail(emailToUse);
      setUserEmail(emailToUse);
    }

    setIsSubmittingComment(true);
    try {
      const added = await roadmapService.addComment(
        partnerId,
        selectedItem._id,
        commentText.trim(),
        emailToUse.split("@")[0] || "User",
        emailToUse
      );
      if (added) {
        setCommentText("");
        setEmailError("");
        const updatedComments = [...(selectedItem.comments || []), added];
        setSelectedItem((prev) => ({ ...prev, comments: updatedComments }));
        setItems((prev) =>
          prev.map((it) =>
            it._id === selectedItem._id ? { ...it, comments: updatedComments } : it
          )
        );
      }
    } catch (err) {
      console.error("Comment submit failed:", err);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  // Step 1 Submit: Save idea, show AI typing animation, then advance to step 2
  const handleStep1Submit = (e) => {
    if (e) e.preventDefault();
    const text = ideaText.trim();
    if (!text || isBotTyping) return;

    setSubmittedIdea(text);
    setIsBotTyping(true);

    setTimeout(() => {
      setIsBotTyping(false);
      setStep(2);
    }, 900);
  };

  // Step 2 Submit: Validate email, show AI typing animation, search similar requests, then advance to step 3
  const handleStep2Submit = async () => {
    if (isBotTyping) return;
    const emailToValidate = userEmail.trim();
    if (!emailToValidate) {
      setEmailError("Please enter your email address");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailToValidate)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setEmailError("");
    setVoterEmail(emailToValidate);
    setSubmittedEmail(emailToValidate);
    setIsBotTyping(true);
    setIsSearchingMatches(true);

    try {
      const [matches] = await Promise.all([
        roadmapService.findSimilarRequests(partnerId, ideaText),
        new Promise((resolve) => setTimeout(resolve, 1000))
      ]);
      setSimilarItems(matches || []);
    } catch (err) {
      console.error("Duplicate search failed:", err);
    } finally {
      setIsBotTyping(false);
      setIsSearchingMatches(false);
      setStep(3);
    }
  };

  // Final submission of user idea
  const handleFinalSubmitIdea = async () => {
    if (!ideaText.trim() || !partnerId) return;
    setIsSubmittingIdea(true);
    try {
      const fullDescription = moreDetails.trim()
        ? `${ideaText.trim()}\n\n${moreDetails.trim()}`
        : ideaText.trim();

      const created = await roadmapService.createFeatureRequest(partnerId, {
        title: ideaText.trim().length > 100 ? ideaText.trim().substring(0, 100) + "..." : ideaText.trim(),
        description: fullDescription,
        email: userEmail.trim()
      });

      if (created) {
        setSubmissionSuccess(true);
        setTimeout(() => {
          setSubmissionSuccess(false);
          setMode("feed");
          setStep(1);
          setIdeaText("");
          setSubmittedIdea("");
          setMoreDetails("");
          setSubmittedEmail("");
          setSimilarItems([]);
          setIsBotTyping(false);
          loadItems();
        }, 1300);
      }
    } catch (err) {
      console.error("Submission failed:", err);
    } finally {
      setIsSubmittingIdea(false);
    }
  };

  // Format date helper: "17. Feb 2026 15:39"
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      const day = d.getDate();
      const month = d.toLocaleDateString("en-US", { month: "short" });
      const year = d.getFullYear();
      const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
      return `${day}. ${month} ${year} ${time}`;
    } catch {
      return "";
    }
  };

  // Format comment date helper: "09. Jul 2025"
  const formatCommentDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      const day = String(d.getDate()).padStart(2, "0");
      const month = d.toLocaleDateString("en-US", { month: "short" });
      const year = d.getFullYear();
      return `${day}. ${month} ${year}`;
    } catch {
      return "";
    }
  };

  // Avatar color generator for user initials
  const getAvatarColor = (name) => {
    const colors = [
      "bg-red-500",
      "bg-emerald-500",
      "bg-blue-500",
      "bg-indigo-500",
      "bg-purple-500",
      "bg-amber-500",
      "bg-rose-500"
    ];
    if (!name) return colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Status badge style helper
  const getStatusBadge = (status) => {
    switch (status) {
      case "Released":
        return {
          bg: "bg-emerald-50 text-emerald-600 border-emerald-200/80",
          dotColor: "bg-emerald-500",
          label: "Released"
        };
      case "In progress":
        return {
          bg: "bg-purple-50 text-purple-600 border-purple-200/80",
          dotColor: "bg-purple-500",
          label: "In progress"
        };
      case "Planned":
        return {
          bg: "bg-blue-50 text-blue-600 border-blue-200/80",
          dotColor: "bg-blue-600",
          label: "Planned"
        };
      case "Requested":
        return {
          bg: "bg-emerald-50 text-emerald-600 border-emerald-200/80",
          dotColor: "bg-emerald-500",
          label: "Requested"
        };
      case "Ideas":
        return {
          bg: "bg-teal-50 text-teal-600 border-teal-200/80",
          dotColor: "bg-teal-500",
          label: "Ideas"
        };
      default:
        return {
          bg: "bg-slate-50 text-slate-600 border-slate-200/80",
          dotColor: "bg-slate-400",
          label: status || "Requested"
        };
    }
  };

  return (
    <div className="flex flex-col h-full w-full bg-white relative overflow-hidden">
      {/* ========================================================= */}
      {/* 1. TOP HEADER (Matches Gleap with both Back & Close icons) */}
      {/* ========================================================= */}
      <div className="flex items-center justify-between px-4 h-12 border-b border-slate-100 shrink-0 sticky top-0 z-30 bg-white">
        <button
          type="button"
          onClick={() => {
            if (mode === "detail") {
              setMode("feed");
              setSelectedItem(null);
            } else if (mode === "request") {
              setMode("feed");
              setStep(1);
              setIdeaText("");
              setSubmittedIdea("");
              setMoreDetails("");
              setSubmittedEmail("");
              setSimilarItems([]);
              setIsBotTyping(false);
            } else {
              const fallback = getFallbackBackView(setupDetails);
              setView(fallback.view);
              if (setActiveIcon) setActiveIcon(fallback.icon);
            }
          }}
          className="w-8 h-8 rounded-full flex items-center justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
          title="Back"
        >
          <ChevronLeft className="w-5 h-5 stroke-[2.2]" />
        </button>

        {/* Center title: clean & matches Gleap (empty in detail view) */}
        <div className="font-semibold text-slate-900 text-sm tracking-tight text-center truncate px-2 flex-1">
          {mode === "request" ? "Request a feature" : mode === "feed" ? "Roadmap" : ""}
        </div>

        <div className="flex items-center gap-1 min-w-[32px] justify-end">
          <button
            type="button"
            onClick={() => {
              if (setIsOpen) setIsOpen(false);
            }}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
            title="Close"
          >
            <X className="w-4 h-4 stroke-[2.2]" />
          </button>
        </div>
      </div>

      {/* ========================================================= */}
      {/* 2. MODE: ROADMAP FEED VIEW                                */}
      {/* ========================================================= */}
      {mode === "feed" && (
        <div className="flex-1 flex flex-col relative overflow-hidden">
          {/* Filter Bar (Recent & Status Dropdowns) */}
          <div className="shrink-0 px-4 py-2.5 bg-white flex items-center gap-3 border-b border-slate-100/80 z-10">
            {/* Sort Dropdown Button */}
            <div className="relative flex-1">
              <button
                type="button"
                onClick={() => {
                  setIsSortDropdownOpen(!isSortDropdownOpen);
                  setIsStatusDropdownOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-1.5 rounded-xl bg-slate-100/70 hover:bg-slate-100 text-xs font-medium text-slate-800 transition-colors border-none cursor-pointer"
              >
                <div className="flex items-center gap-2 truncate">
                  <Clock className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                  <span className="capitalize">
                    {SORT_OPTIONS.find((s) => s.id === activeSort)?.label || "Recent"}
                  </span>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              </button>

              {/* Sort Menu */}
              {isSortDropdownOpen && (
                <div className="absolute top-full left-0 mt-1.5 w-44 bg-white rounded-2xl shadow-xl border border-slate-100 py-1.5 z-30 animate-in fade-in zoom-in-95 duration-150">
                  {SORT_OPTIONS.map((opt) => {
                    const IconComp = opt.icon;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => {
                          setActiveSort(opt.id);
                          setIsSortDropdownOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-3.5 py-2 text-xs text-left transition-colors border-none bg-transparent cursor-pointer ${
                          activeSort === opt.id
                            ? "font-bold text-blue-600 bg-blue-50/60"
                            : "text-slate-700 hover:bg-slate-50 font-medium"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <IconComp className="w-3.5 h-3.5 text-slate-500" />
                          <span>{opt.label}</span>
                        </div>
                        {activeSort === opt.id && <Check className="w-4 h-4 text-blue-600" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Status Dropdown Button */}
            <div className="relative flex-1">
              <button
                type="button"
                onClick={() => {
                  setIsStatusDropdownOpen(!isStatusDropdownOpen);
                  setIsSortDropdownOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-1.5 rounded-xl bg-slate-100/70 hover:bg-slate-100 text-xs font-medium text-slate-800 transition-colors border-none cursor-pointer"
              >
                <div className="flex items-center gap-2 truncate">
                  <span
                    className={`w-2 h-2 rounded-full shrink-0 ${
                      STATUS_OPTIONS.find((s) => s.id === activeStatus)?.dotColor || "bg-teal-500"
                    }`}
                  />
                  <span>
                    {STATUS_OPTIONS.find((s) => s.id === activeStatus)?.label || "Ideas"}
                  </span>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              </button>

              {/* Status Menu */}
              {isStatusDropdownOpen && (
                <div className="absolute top-full right-0 mt-1.5 w-44 bg-white rounded-2xl shadow-xl border border-slate-100 py-1.5 z-30 animate-in fade-in zoom-in-95 duration-150">
                  {STATUS_OPTIONS.map((st) => (
                    <button
                      key={st.id}
                      onClick={() => {
                        setActiveStatus(st.id);
                        setIsStatusDropdownOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3.5 py-2 text-xs text-left transition-colors border-none bg-transparent cursor-pointer ${
                        activeStatus === st.id
                          ? "font-bold text-blue-600 bg-blue-50/60"
                          : "text-slate-700 hover:bg-slate-50 font-medium"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${st.dotColor}`} />
                        <span>{st.label}</span>
                      </div>
                      {activeStatus === st.id && <Check className="w-4 h-4 text-blue-600" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Feed List Container */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-3 pb-24 space-y-2.5 scroll-smooth"
          >
            {loading ? (
              /* Shimmer Skeletons */
              <div className="space-y-2.5">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="p-3 bg-white rounded-2xl border border-slate-100 flex items-start gap-3 animate-pulse"
                  >
                    <div className="w-9 h-11 bg-slate-100 rounded-xl shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3.5 bg-slate-200 rounded w-3/4" />
                      <div className="h-2.5 bg-slate-100 rounded w-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : items.length > 0 ? (
              /* Roadmap Items */
              items.map((item) => (
                <div
                  key={item._id}
                  onClick={() => {
                    setSelectedItem(item);
                    setIsDescExpanded(false);
                    setMode("detail");
                  }}
                  className="p-3 bg-white hover:bg-slate-50/80 rounded-2xl border border-slate-200/80 hover:border-slate-300 shadow-[0_1px_3px_rgba(0,0,0,0.02)] transition-all flex items-start gap-3 cursor-pointer group"
                >
                  {/* Upvote Button Pill */}
                  <button
                    type="button"
                    onClick={(e) => handleVoteToggle(e, item)}
                    className={`flex flex-col items-center justify-center w-9 h-11 rounded-xl border transition-all shrink-0 cursor-pointer ${
                      item.hasVoted
                        ? "shadow-sm"
                        : "bg-white hover:bg-slate-50 border-slate-200/90 text-slate-700"
                    }`}
                    style={
                      item.hasVoted
                        ? { backgroundColor: primaryColor, borderColor: primaryColor, color: "#ffffff" }
                        : {}
                    }
                    title="Upvote"
                  >
                    <ChevronUp
                      className={`w-3.5 h-3.5 ${
                        item.hasVoted ? "stroke-[3]" : "text-slate-500 stroke-[2.2]"
                      }`}
                    />
                    <span className="text-[11px] font-bold leading-none mt-0.5">
                      {item.voteCount || 0}
                    </span>
                  </button>

                  {/* Right Content */}
                  <div className="flex-1 min-w-0 pr-1">
                    <h3 className="text-xs font-bold text-slate-900 group-hover:text-blue-600 transition-colors line-clamp-1 leading-snug">
                      {item.title}
                    </h3>
                    <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 leading-relaxed">
                      {item.description}
                    </p>

                    {/* Status Badge & Comments */}
                    <div className="flex items-center gap-2 mt-1.5">
                      {(() => {
                        const badge = getStatusBadge(item.status);
                        return (
                          <span
                            className={`inline-flex items-center gap-1 text-[9px] font-medium px-2 py-0.5 rounded-full border ${badge.bg}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${badge.dotColor}`} />
                            <span>{badge.label}</span>
                          </span>
                        );
                      })()}

                      {item.comments?.length > 0 && (
                        <span className="flex items-center gap-1 text-[10px] text-slate-400 font-medium ml-0.5">
                          <MessageSquare className="w-3 h-3 text-slate-400" />
                          {item.comments.length}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              /* Empty state */
              <div className="py-16 text-center bg-slate-50/50 rounded-2xl border border-dashed border-slate-200 p-6">
                <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mx-auto mb-3">
                  <MapPin className="w-6 h-6" />
                </div>
                <h3 className="text-xs font-bold text-slate-800">No feature requests found</h3>
                <p className="text-[11px] text-slate-500 mt-1 max-w-xs mx-auto">
                  Be the first to suggest a new feature or idea to shape our roadmap!
                </p>
              </div>
            )}
          </div>

          {/* Floating Action Button: + Request a feature */}
          <div className="absolute bottom-[72px] left-0 right-0 flex justify-center pointer-events-none z-30">
            <button
              type="button"
              onClick={() => {
                setMode("request");
                setStep(1);
                setIdeaText("");
                setSubmittedIdea("");
                setMoreDetails("");
                setSubmittedEmail("");
                setSimilarItems([]);
                setIsBotTyping(false);
              }}
              className="pointer-events-auto flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-xs font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95 cursor-pointer border-none"
              style={{ backgroundColor: primaryColor }}
            >
              <Plus className="w-4 h-4 stroke-[2.5]" />
              <span>Request a feature</span>
            </button>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* 3. MODE: CONVERSATIONAL REQUEST FLOW (Matching Gleap UI)  */}
      {/* ========================================================= */}
      {mode === "request" && (
        <div className="flex-1 flex flex-col justify-between overflow-hidden bg-white">
          <div
            ref={chatScrollRef}
            className="flex-1 min-h-0 overflow-y-auto p-4 pb-32 space-y-3 scroll-smooth"
          >
            {/* Bot message bubbles from step 1 (No AI avatar, matches Gleap Image 1) */}
            <div className="flex flex-col gap-1.5 max-w-[85%]">
              <div className="bg-slate-100/90 text-slate-900 rounded-2xl rounded-tl-sm px-4 py-2.5 text-xs leading-relaxed font-normal">
                Let us know what you would like to see next.
              </div>
              <div className="bg-slate-100/90 text-slate-900 rounded-2xl rounded-tl-sm px-4 py-2.5 text-xs leading-relaxed font-normal">
                What would make {partnerName} more useful?
              </div>
            </div>

            {/* User message 1: ONLY SHOWS AFTER STEP 1 IS SUBMITTED */}
            {submittedIdea && (
              <div className="flex justify-end animate-in fade-in duration-200">
                <div
                  className="rounded-2xl rounded-tr-sm px-4 py-2.5 text-xs max-w-[82%] leading-relaxed font-normal shadow-sm"
                  style={{ backgroundColor: primaryColor, color: "#ffffff" }}
                >
                  {submittedIdea}
                </div>
              </div>
            )}

            {/* AI Typing Indicator */}
            {isBotTyping && (
              <div className="flex items-center gap-1.5 bg-slate-100/90 text-slate-500 rounded-2xl rounded-tl-sm px-4 py-3 w-16 animate-in fade-in duration-200">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            )}

            {/* Bot message 2 (Asking for details, matches Gleap Image 2) */}
            {step >= 2 && (
              <div className="flex flex-col max-w-[85%] animate-in fade-in duration-200">
                <div className="bg-slate-100/90 text-slate-900 rounded-2xl rounded-tl-sm px-4 py-2.5 text-xs leading-relaxed font-normal">
                  Great! Any details you want to add to your feature request?
                </div>
              </div>
            )}

            {/* User message 2 (Email, matches Gleap Image 3) */}
            {submittedEmail && (
              <div className="flex justify-end animate-in fade-in duration-200">
                <div
                  className="rounded-2xl rounded-tr-sm px-4 py-2.5 text-xs max-w-[82%] leading-relaxed font-normal shadow-sm"
                  style={{ backgroundColor: primaryColor, color: "#ffffff" }}
                >
                  {submittedEmail}
                </div>
              </div>
            )}

            {/* Bot message 3 & Similar Requests Card (Matches Gleap Image 3 & Image 2) */}
            {step >= 3 && !isBotTyping && (
              <div className="space-y-3 animate-in fade-in duration-300 max-w-[85%] w-full">
                <div className="bg-slate-100/90 text-slate-900 rounded-2xl rounded-tl-sm px-4 py-2.5 text-xs leading-relaxed font-normal">
                  {similarItems.length > 0
                    ? "We found existing feature requests that might align with your idea. Upvote the ones you like or go ahead and submit your own idea."
                    : "Great! We checked existing requests and didn't find any direct duplicates. You can now submit your request:"}
                </div>

                {/* Elevated Suggestions Card: Aligned to width of agent reply */}
                <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-1.5 space-y-1 w-full">
                  {similarItems.map((match) => (
                    <div
                      key={match._id}
                      onClick={() => {
                        setSelectedItem(match);
                        setIsDescExpanded(false);
                        setMode("detail");
                      }}
                      className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-xl transition-colors cursor-pointer group"
                    >
                      {/* Left Upvote Pill Button */}
                      <button
                        type="button"
                        onClick={(e) => handleVoteToggle(e, match)}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all border-none cursor-pointer shrink-0 ${
                          match.hasVoted
                            ? "shadow-sm"
                            : "bg-slate-100 hover:bg-slate-200 text-slate-800"
                        }`}
                        style={
                          match.hasVoted
                            ? { backgroundColor: primaryColor, color: "#ffffff" }
                            : {}
                        }
                      >
                        <ChevronUp className="w-3.5 h-3.5 stroke-[2.5]" />
                        <span>{match.voteCount || 0}</span>
                      </button>

                      {/* Middle Title */}
                      <span className="flex-1 text-xs font-normal text-slate-900 px-3 truncate group-hover:text-blue-600 transition-colors">
                        {match.title}
                      </span>

                      {/* Right Chevron */}
                      <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  ))}

                  {/* Submit my idea row */}
                  <div
                    onClick={handleFinalSubmitIdea}
                    className={`flex items-center justify-between p-2 hover:bg-slate-50 rounded-xl transition-colors cursor-pointer group ${
                      similarItems.length > 0 ? "border-t border-slate-100 mt-1 pt-2" : ""
                    }`}
                  >
                    <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-700 group-hover:bg-slate-200 shrink-0">
                      <Plus className="w-4 h-4 stroke-[2.5]" />
                    </div>
                    <span className="flex-1 text-xs font-normal text-slate-900 px-3">
                      Submit my idea
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>

                {/* Success feedback */}
                {submissionSuccess && (
                  <div className="p-3 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-semibold flex items-center gap-2 animate-in fade-in">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Feature request submitted successfully!</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bottom input area: Step 1 (Explain your request, Image 1) */}
          {step === 1 && (
            <div className="p-4 pb-20 border-t border-slate-100 bg-white shrink-0">
              <form
                onSubmit={handleStep1Submit}
                className="flex items-center bg-white border border-slate-200/90 rounded-2xl p-1.5 focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-100 transition-all shadow-sm"
              >
                <input
                  type="text"
                  value={ideaText}
                  onChange={(e) => setIdeaText(e.target.value)}
                  placeholder="Explain your request."
                  autoFocus
                  disabled={isBotTyping}
                  className="flex-1 px-3 py-2 text-xs bg-transparent border-none outline-none text-slate-900 placeholder-slate-400 font-normal"
                />
                <button
                  type="submit"
                  disabled={!ideaText.trim() || isBotTyping}
                  className="w-8 h-8 rounded-xl bg-slate-100 hover:bg-slate-200 disabled:opacity-40 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-all border-none cursor-pointer"
                >
                  <ChevronRight className="w-4 h-4 stroke-[2.5]" />
                </button>
              </form>
            </div>
          )}

          {/* Bottom input area: Step 2 (More details, Email & Submit, Image 2) */}
          {step === 2 && (
            <div className="p-4 pb-20 border-t border-slate-100 bg-white shrink-0 animate-in fade-in duration-200">
              <div className="space-y-2.5">
                <textarea
                  rows={3}
                  value={moreDetails}
                  onChange={(e) => setMoreDetails(e.target.value)}
                  placeholder="The more details, the better."
                  className="w-full px-3.5 py-2.5 text-xs bg-white border border-slate-200/90 rounded-2xl outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 resize-none text-slate-900 placeholder-slate-400 transition-all font-normal"
                />
                <input
                  type="email"
                  value={userEmail}
                  onChange={(e) => {
                    setUserEmail(e.target.value);
                    setEmailError("");
                  }}
                  placeholder="Your e-mail"
                  className="w-full px-3.5 py-2.5 text-xs bg-white border border-slate-200/90 rounded-2xl outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 text-slate-900 placeholder-slate-400 transition-all font-normal"
                />
                {emailError && (
                  <p className="text-[11px] text-red-500 font-medium px-1 m-0">{emailError}</p>
                )}
                <button
                  type="button"
                  disabled={!userEmail.trim() || isSearchingMatches}
                  onClick={handleStep2Submit}
                  className="w-full py-3 rounded-2xl text-xs font-semibold transition-all border-none cursor-pointer disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed shadow-sm"
                  style={
                    userEmail.trim()
                      ? { backgroundColor: primaryColor, color: "#ffffff" }
                      : {}
                  }
                >
                  {isSearchingMatches ? "Checking similar requests..." : "Submit"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========================================================= */}
      {/* 4. MODE: ITEM DETAIL VIEW (Exact Match to Gleap Screen)   */}
      {/* ========================================================= */}
      {mode === "detail" && selectedItem && (
        <div className="flex-1 flex flex-col justify-between overflow-hidden bg-white">
          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-3.5 scroll-smooth">
            {/* Top Hero: Upvote Pill + Title + Metadata */}
            <div className="flex items-stretch gap-3.5">
              {/* Upvote Pill */}
              <button
                type="button"
                onClick={(e) => handleVoteToggle(e, selectedItem)}
                className={`flex flex-col items-center justify-center w-11 min-h-[48px] py-1.5 rounded-2xl border transition-all shrink-0 cursor-pointer ${
                  selectedItem.hasVoted
                    ? "shadow-sm"
                    : "bg-white hover:bg-slate-50 border-slate-200/90 text-slate-800"
                }`}
                style={
                  selectedItem.hasVoted
                    ? { backgroundColor: primaryColor, borderColor: primaryColor, color: "#ffffff" }
                    : {}
                }
                title="Upvote"
              >
                <ChevronUp
                  className={`w-4 h-4 ${
                    selectedItem.hasVoted ? "stroke-[3]" : "text-slate-600 stroke-[2.5]"
                  }`}
                />
                <span className="text-xs font-bold leading-none mt-1">
                  {selectedItem.voteCount || 0}
                </span>
              </button>

              <div className="flex-1 min-w-0 flex flex-col justify-between">
                <h2 className="text-sm sm:text-[14.5px] font-bold text-slate-900 tracking-tight leading-snug">
                  {selectedItem.title}
                </h2>

                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  {/* Status Badge */}
                  {(() => {
                    const badge = getStatusBadge(selectedItem.status);
                    return (
                      <span
                        className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${badge.bg}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${badge.dotColor}`} />
                        <span>{badge.label}</span>
                      </span>
                    );
                  })()}

                  <span className="text-xs text-slate-400 font-normal">
                    {formatDate(selectedItem.createdAt)}
                  </span>
                </div>
              </div>
            </div>

            {/* Description Section */}
            <div className="pt-2">
              <h4 className="text-xs font-bold text-slate-500 mb-1">
                Description
              </h4>
              <div className="text-xs text-slate-700 leading-relaxed font-normal whitespace-pre-wrap">
                {selectedItem.description && selectedItem.description.length > 220 && !isDescExpanded
                  ? `${selectedItem.description.substring(0, 220)}...`
                  : selectedItem.description}
              </div>
              {selectedItem.description && selectedItem.description.length > 220 && (
                <button
                  type="button"
                  onClick={() => setIsDescExpanded(!isDescExpanded)}
                  className="text-xs font-bold text-slate-900 hover:underline border-none bg-transparent cursor-pointer p-0 mt-1 block"
                >
                  {isDescExpanded ? "Show less" : "Show more"}
                </button>
              )}
            </div>

            <div className="border-b border-slate-100/90 pt-1" />

            {/* Comments Thread matching Gleap speech bubbles */}
            <div className="space-y-3.5 pt-1">
              {selectedItem.comments && selectedItem.comments.length > 0 ? (
                selectedItem.comments.map((c, idx) => (
                  <div key={idx} className="flex items-start gap-2.5">
                    {/* Avatar */}
                    <div className="relative shrink-0 mt-0.5">
                      <div
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                          c.isAdmin ? "bg-slate-900" : getAvatarColor(c.author)
                        }`}
                      >
                        {c.author?.charAt(0).toUpperCase() || "U"}
                      </div>
                      {c.isAdmin && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-white rounded-full flex items-center justify-center shadow-sm">
                          <CheckCircle2 className="w-3 h-3 text-slate-950 stroke-[2.5]" />
                        </div>
                      )}
                    </div>

                    {/* Speech Bubble */}
                    <div className="flex-1 bg-slate-100/80 rounded-2xl px-3.5 py-2.5 text-xs leading-relaxed max-w-[85%]">
                      <p className="text-slate-800 whitespace-pre-wrap font-normal m-0">
                        {c.message}
                      </p>
                      <div className="text-[11px] text-slate-400 mt-2 font-normal">
                        {formatCommentDate(c.createdAt)}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-4 text-center text-slate-400 text-xs font-normal">
                  No comments yet. Be the first to reply!
                </div>
              )}
            </div>
          </div>

          {/* Bottom Comment Input Box matching Screenshot 2 */}
          <div className="p-4 pb-20 border-t border-slate-100 bg-white shrink-0">
            <div className="space-y-2">
              {!userEmail && (
                <div>
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => {
                      setEmailInput(e.target.value);
                      setEmailError("");
                    }}
                    placeholder="Your email (required to comment)..."
                    className="w-full px-3.5 py-2 text-xs bg-white border border-slate-200/90 rounded-xl outline-none focus:border-slate-400 text-slate-800 placeholder-slate-400 font-normal"
                  />
                  {emailError && (
                    <p className="text-[11px] text-red-500 font-medium px-1 mt-1 mb-0">{emailError}</p>
                  )}
                </div>
              )}

              <textarea
                rows={3}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Write a comment"
                className="w-full p-3 text-xs bg-white border border-slate-200/90 rounded-2xl outline-none focus:border-slate-400 text-slate-800 placeholder-slate-400 resize-none font-normal leading-relaxed"
              />

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleSendComment}
                  disabled={!commentText.trim() || isSubmittingComment}
                  className="px-5 py-2 rounded-xl text-xs font-semibold text-white transition-all border-none cursor-pointer disabled:opacity-40 shadow-sm"
                  style={{
                    backgroundColor: commentText.trim() ? primaryColor : "#64748b"
                  }}
                >
                  {isSubmittingComment ? "Sending..." : "Send"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Roadmap;
